import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { interpolateString, insertIntoNativeInput } from '../../lib/utils';
import { UserFunction, EditorType, HostImage } from '../../lib/types';
import { CodeEditor, CodeEditorRef } from '../shared-ui/CodeEditor';
import { ToolsPanel } from '../shared-ui/ToolsPanel';
import { PanelRightClose, PanelRightOpen, Wand2, Globe } from 'lucide-react';

interface HtmlEditorProps {
    content: string;
    onChange: (val: string) => void;
    
    // Store Props
    variablesJson: string;
    onVariablesChange: (json: string) => void;
    functions: UserFunction[];
    onFunctionsChange: (funcs: UserFunction[]) => void;
    
    // Optional
    hostImages?: HostImage[];
    onAddImage?: (img: HostImage) => void;
    onDeleteImage?: (id: string) => void;

    // AI Prop
    onAiAssist?: (prompt: string) => Promise<string>;

    // Visibility
    enablePreview?: boolean;
    showVariables?: boolean;
    showFunctions?: boolean;
    showBlocks?: boolean;
    showImages?: boolean;
    showAi?: boolean;
}

const EMPTY_FUNCTIONS: UserFunction[] = [];
const EMPTY_IMAGES: HostImage[] = [];

export const HtmlEditor: React.FC<HtmlEditorProps> = ({
    content = '',
    onChange,
    variablesJson = '{}',
    onVariablesChange,
    functions = EMPTY_FUNCTIONS,
    onFunctionsChange,
    hostImages = EMPTY_IMAGES,
    onAddImage,
    onDeleteImage,
    onAiAssist,
    enablePreview = true,
    showVariables = true,
    showFunctions = true,
    showBlocks = true,
    showImages = true,
    showAi = true
}) => {
    // Preview State
    const [previewContent, setPreviewContent] = useState<string>('');
    const [error, setError] = useState<string | null>(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(true);
    const [missingFunctions, setMissingFunctions] = useState<string[]>([]);

    // Internal Variable Parsing
    const variablesObj = useMemo(() => {
        try {
            return JSON.parse(variablesJson);
        } catch {
            return {};
        }
    }, [variablesJson]);

    // Editor Ref
    const editorRef = useRef<CodeEditorRef>(null);

    // Resize & Layout State
    const [previewWidth, setPreviewWidth] = useState(600);
    const [containerWidth, setContainerWidth] = useState(0);
    const [isResizing, setIsResizing] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const startResizing = useCallback(() => setIsResizing(true), []);
    const stopResizing = useCallback(() => setIsResizing(false), []);

    // Layout Breakpoint
    const isStacked = containerWidth < 850;

    useEffect(() => {
        if (!containerRef.current) return;
        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                setContainerWidth(entry.contentRect.width);
            }
        });
        observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!isResizing || !containerRef.current) return;
            const containerRect = containerRef.current.getBoundingClientRect();
            const newWidth = containerRect.right - e.clientX;
            
            if (newWidth > 300 && newWidth < containerRect.width - 200) {
                setPreviewWidth(newWidth);
            }
        };

        if (isResizing) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', stopResizing);
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
        } else {
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', stopResizing);
        };
    }, [isResizing, stopResizing]);

    // Check for missing functions
    useEffect(() => {
        const safeFunctions = Array.isArray(functions) ? functions : [];
        const regex = /{{#func:([a-zA-Z0-9_]+)\(/g;
        const missing: Set<string> = new Set();
        let match;
        while ((match = regex.exec(content)) !== null) {
            const funcName = match[1];
            if (!safeFunctions.some(f => f.name === funcName)) {
                missing.add(funcName);
            }
        }
        setMissingFunctions(Array.from(missing));
    }, [content, functions]);

    useEffect(() => {
        if (!enablePreview) return;
        try {
            const imagesContext = (hostImages || []).reduce((acc, img) => {
                acc[img.name] = img.url;
                return acc;
            }, {} as Record<string, string>);

            const combinedContext = {
                ...variablesObj,
                images: imagesContext
            };

            const interpolated = interpolateString(content, combinedContext, functions || []);
            setPreviewContent(interpolated);
            setError(null);
        } catch (e: any) {
            setError(e.message);
        }
    }, [content, variablesObj, functions, hostImages, enablePreview]);

    const handleInsert = (text: string) => {
        // Prioritize Monaco Editor if it has focus
        if (editorRef.current && editorRef.current.hasTextFocus()) {
            editorRef.current.insertText(text);
            return;
        }
        
        insertIntoNativeInput(document.activeElement, text);
    };

    return (
        <div className="flex h-full w-full">
            <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden" ref={containerRef}>
                {/* Header Toolbar */}
                <div className="h-12 border-b border-slate-200 bg-white flex items-center justify-between px-4 shrink-0 shadow-sm z-10">
                     <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2 text-teal-700 font-bold text-sm">
                            <Globe size={16} />
                            <span>HTML Editor</span>
                        </div>
                     </div>
                </div>

                <div className={`flex-1 flex ${isStacked ? 'flex-col' : 'flex-row'} gap-0 min-h-0 relative`}>
                     {/* Source Editor */}
                     <div className={`flex-1 flex flex-col min-h-0 p-4 min-w-0`}>
                        <div className="text-xs font-bold text-slate-500 mb-2 uppercase tracking-wider flex justify-between items-center h-6">
                            <span>HTML Source</span>
                            <div className="flex items-center gap-3">
                                <button 
                                    onClick={() => editorRef.current?.format()}
                                    className="text-slate-400 hover:text-teal-600 hover:bg-teal-50 p-0.5 rounded transition-colors"
                                    title="Format Code (Ctrl+F)"
                                >
                                    <Wand2 size={14} />
                                </button>
                                <span className="text-teal-600 font-mono text-[10px]">Handlebars</span>
                                {enablePreview && (
                                    <button 
                                        onClick={() => setIsPreviewOpen(!isPreviewOpen)}
                                        className="text-slate-400 hover:text-slate-600 hover:bg-slate-100 p-0.5 rounded transition-colors"
                                        title={isPreviewOpen ? "Collapse Preview" : "Show Preview"}
                                    >
                                        {isPreviewOpen ? <PanelRightClose size={14} /> : <PanelRightOpen size={14} />}
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="flex-1 min-h-0 relative">
                             <CodeEditor 
                                ref={editorRef}
                                language={'html'} 
                                value={content} 
                                onChange={(val) => onChange(val || '')} 
                            />
                        </div>
                    </div>

                    {/* Resizable Preview */}
                    {enablePreview && isPreviewOpen && (
                        <>
                             {/* Resize Handle - Only when not stacked */}
                             {!isStacked && (
                                <div
                                    role="separator"
                                    className="w-1 bg-slate-200 hover:bg-teal-400 cursor-col-resize z-10 hover:w-1.5 -ml-0.5 transition-all flex items-center justify-center group flex-shrink-0"
                                    onMouseDown={startResizing}
                                >
                                    <div className="h-8 w-1 bg-slate-400 rounded-full group-hover:bg-white/80 hidden group-hover:block" />
                                </div>
                             )}

                            <div 
                                className="flex flex-col min-h-0 bg-slate-50/50 p-4 overflow-hidden flex-shrink-0"
                                style={{ 
                                    width: isStacked ? '100%' : previewWidth,
                                    height: isStacked ? '50%' : '100%',
                                    borderTop: isStacked ? '1px solid #e2e8f0' : 'none',
                                    borderLeft: isStacked ? 'none' : undefined
                                }}
                            >
                                <div className="flex justify-between items-center mb-2 h-6">
                                    <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Browser Preview</div>
                                    {error && <span className="text-xs text-red-600 font-medium bg-red-50 px-2 py-1 rounded truncate max-w-[300px]">{error}</span>}
                                </div>
                                
                                <div className="flex-1 bg-white rounded-xl border border-slate-300 overflow-hidden relative shadow-sm">
                                    <iframe 
                                        title="html-preview"
                                        srcDoc={previewContent}
                                        className="w-full h-full border-none"
                                        sandbox="allow-same-origin"
                                    />
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Tools Panel */}
            <ToolsPanel 
                variablesJson={variablesJson}
                onVariablesChange={onVariablesChange}
                functions={functions}
                onFunctionsChange={onFunctionsChange}
                activeEditorType={EditorType.HTML_PAGE}
                missingFunctions={missingFunctions}
                hostImages={hostImages}
                onAddImage={onAddImage}
                onDeleteImage={onDeleteImage}
                onInsert={handleInsert}
                onUpdateContent={(val) => onChange(val)}
                onAiAssist={onAiAssist}
                showVariables={showVariables}
                showFunctions={showFunctions}
                showBlocks={showBlocks}
                showImages={showImages}
                showChat={showAi}
            />
        </div>
    );
};